import React, {useState, useEffect, useMemo} from 'react';
import {
    Box, Typography, Button, Paper, TextField, IconButton, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
    Divider, Alert, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ViewerContainer from '../PdfViewer/ViewerContainer/ViewerContainer';
import {API_BASE} from '../../apiBase';

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

export default function GroundTruthAnnotator({document, onClose, onSave}) {
    const headers = useAuthHeaders();
    const [fields, setFields] = useState({});
    const [pdfFile, setPdfFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedAnnotations, setSelectedAnnotations] = useState([]);
    const [allAnnotations, setAllAnnotations] = useState([]);
    const [groupMeta, setGroupMeta] = useState({});
    const [hasExistingAnnotations, setHasExistingAnnotations] = useState(false);

    useEffect(() => {
        if (document) {
            setFields(document.ground_truth || {});

            // Convert ground truth to annotations format
            const annotations = Object.entries(document.ground_truth || {})
                .filter(([_, fieldData]) => fieldData.bbox)
                .map(([fieldName, fieldData]) => ({
                    type: 'entity',
                    label: fieldName,
                    text: fieldData.value || '',
                    bbox: fieldData.bbox,
                    page: fieldData.page || 1,
                    entity_type: fieldName
                }));

            // Check if there are existing annotations
            const hasAnnotations = annotations.length > 0;
            setHasExistingAnnotations(hasAnnotations);
            
            setAllAnnotations(annotations);
            // Also initialize selected annotations with existing ground truth
            setSelectedAnnotations(annotations);

            loadPdfFile();
        }
    }, [document]);



    const loadPdfFile = async () => {
        if (!document) return;

        setLoading(true);
        try {
            // Construct PDF URL
            const pathParts = document.document_path.split('/uploaded_files/');
            if (pathParts.length > 1) {
                const pdfUrl = `${API_BASE}/evaluation/files/${pathParts[1]}`;

                // Fetch as blob and convert to File
                const response = await fetch(pdfUrl, {headers});
                const blob = await response.blob();
                const file = new File([blob], document.name + '.pdf', {type: 'application/pdf'});
                setPdfFile(file);
            }
        } catch (error) {
            console.error('Error loading PDF:', error);
            alert('Failed to load PDF file');
        } finally {
            setLoading(false);
        }
    };

    // When annotations change in ViewerContainer, update fields (from all annotations)
    const handleAnnotationsChange = (annotations) => {
        setAllAnnotations(annotations);
    };

    // When a new annotation is drawn
    const handleAnnotationAdded = (annotationData) => {
        // Create a new entity annotation from the drawn box
        const newAnnotation = {
            type: 'entity',
            label: `Field_${Date.now()}`, // Temporary label
            text: '',
            bbox: annotationData.bbox,
            page: annotationData.page,
            __raw_bbox: annotationData.__raw_bbox
        };

        // Add to all annotations
        setAllAnnotations(prev => [...prev, newAnnotation]);
        // Also add to selected annotations
        setSelectedAnnotations(prev => [...prev, newAnnotation]);
    };

    const handleSave = async () => {
        try {
            // Only save SELECTED annotations (the ones in the right panel)
            const groundTruthFields = {};
            selectedAnnotations.forEach((ann, index) => {
                // Extract label, text, and bbox based on annotation type
                let label, text, bbox;

                if (ann.type === 'key_value') {
                    // For key_value: label is field.text, value is value.text, bbox is value.bbox
                    label = ann.field?.text || `field_${index + 1}`;
                    text = ann.value?.text || '';
                    bbox = ann.value?.bbox;
                } else if (ann.type === 'entity' || ann.type === 'summary_row') {
                    // For entity/summary_row: label, text, and bbox are at top level
                    label = ann.label || ann.entity_type || `field_${index + 1}`;
                    text = ann.text || '';
                    bbox = ann.bbox;
                } else if (ann.type === 'table' || ann.type === 'table_row') {
                    // Skip tables for ground truth
                    return;
                } else {
                    // Fallback for unknown types
                    label = ann.label || ann.entity_type || `field_${index + 1}`;
                    text = ann.text || '';
                    bbox = ann.bbox;
                }

                if (bbox && label) {
                    // Use __raw_bbox if available (without display offset), otherwise use bbox
                    const bboxToSave = ann.__raw_bbox || bbox;
                    groundTruthFields[label] = {
                        value: text,
                        page: ann.page || 1,
                        bbox: bboxToSave
                    };
                }
            });

            const res = await fetch(`${API_BASE}/evaluation/documents/${document.id}`, {
                method: 'PUT',
                headers: {...headers, 'Content-Type': 'application/json'},
                body: JSON.stringify({ground_truth: groundTruthFields})
            });

            if (res.ok) {
                onSave(groundTruthFields);
            } else {
                const data = await res.json();
                alert(data.error || 'Failed to save ground truth');
            }
        } catch (e) {
            console.error('Error saving ground truth:', e);
            alert('Server error');
        }
    };

    if (!document) return null;

    return (
        <Dialog
            open={Boolean(document)}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    height: '95vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{flexShrink: 0}}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Annotate Ground Truth: {document?.name}</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexGrow: 1}}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress/>
                    </Box>
                ) : pdfFile ? (
                    <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden'}}>
                        <ViewerContainer
                            showHeader
                            compactHeader
                            preventAutoExtract={hasExistingAnnotations}
                            selectedAnnotations={selectedAnnotations}
                            setSelectedAnnotations={setSelectedAnnotations}
                            allAnnotations={allAnnotations}
                            onAllAnnotationsChange={handleAnnotationsChange}
                            onAnnotationAdded={handleAnnotationAdded}
                            groupMeta={groupMeta}
                            setGroupMeta={setGroupMeta}
                            file_input={pdfFile}
                            persistSelection={true}
                            readOnlyHighlights={false}
                        />
                    </Box>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="error">Failed to load PDF</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Alert severity="info" sx={{flex: 1, mr: 2}}>
                    Draw boxes on the PDF to mark field locations. These will be used as ground truth.
                </Alert>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" startIcon={<SaveIcon/>} onClick={handleSave}>
                    Save Ground Truth ({selectedAnnotations.length} selected)
                </Button>
            </DialogActions>
        </Dialog>
    );
}
